import { useEffect, useMemo } from "react"
import { Form } from "antd"
import { useQueries } from "@tanstack/react-query"
import { CustomInput, CustomSelect } from "."
import { useGet } from "utils/useFetch"
import { newArchAxiosInstance } from "utils/request"

const StablecoinPaymentFields = ({
  className = "flex w-full flex-col gap-4 lg:flex-row lg:gap-10",
  stablecoinName = "stablecoin",
  networkName = "network",
  networkCountryCodeName = "networkCountryCode",
  walletAddressName = "walletAddress",
}: {
  className?: string
  stablecoinName?: string
  networkName?: string
  networkCountryCodeName?: string
  walletAddressName?: string
}) => {
  const form = Form.useFormInstance()
  const stablecoin = Form.useWatch(stablecoinName, form)
  const selectedNetworkCode = Form.useWatch(networkName, form)
  const currenciesUrl =
    "/commonutils/getcurrencies?forpayment=false&isCryptoCurrency=true"
  const networksUrl = `/commonutils/getbanks?currency_code=${stablecoin || ""}`
  const { data: currenciesResponse, isLoading: isCurrenciesLoading } = useGet<
    CoreBackendResponse<CryptoCurrency[]>
  >({
    url: currenciesUrl,
    instanceKey: "newArchAxiosInstance",
    queryKey: [currenciesUrl],
  })
  const { data: networksResponse, isLoading: isNetworksLoading } = useGet<
    CoreBackendResponse<StablecoinNetwork[]>
  >({
    url: networksUrl,
    instanceKey: "newArchAxiosInstance",
    queryKey: [networksUrl],
    enabled: !!stablecoin,
  })
  const currencies = useMemo(
    () => getResponseData(currenciesResponse),
    [currenciesResponse]
  )
  const currencyNetworkQueries = useQueries({
    queries: currencies.map((currency) => ({
      queryKey: ["/commonutils/getbanks", currency.code],
      queryFn: async () => {
        const response = await newArchAxiosInstance.get<
          CoreBackendResponse<StablecoinNetwork[]>
        >(`/commonutils/getbanks?currency_code=${currency.code}`)
        return getResponseData(response.data)
      },
      enabled: !!selectedNetworkCode && !stablecoin,
    })),
  })
  const networks = useMemo(
    () => getResponseData(networksResponse),
    [networksResponse]
  )
  const selectedNetwork = useMemo(
    () =>
      networks.find(
        (network) => String(network.code) === String(selectedNetworkCode)
      ),
    [networks, selectedNetworkCode]
  )
  const walletAddressRules = useMemo(
    () => [
      { required: true, message: "Please enter this field" },
      {
        validator: (_: unknown, value?: string) => {
          if (!value) return Promise.resolve()
          if (isValidStablecoinWalletAddress(value, selectedNetwork)) {
            return Promise.resolve()
          }

          return Promise.reject(
            new Error(getWalletAddressValidationMessage(selectedNetwork))
          )
        },
      },
    ],
    [selectedNetwork]
  )

  useEffect(() => {
    if (!selectedNetworkCode || stablecoin) return

    const matchingCurrencyIndex = currencyNetworkQueries.findIndex((query) =>
      query.data?.some(
        (network) => String(network.code) === String(selectedNetworkCode)
      )
    )
    const matchingCurrency = currencies[matchingCurrencyIndex]

    if (matchingCurrency) {
      form.setFieldValue(stablecoinName, matchingCurrency.code)
    }
  }, [
    currencies,
    currencyNetworkQueries,
    form,
    selectedNetworkCode,
    stablecoin,
    stablecoinName,
  ])

  useEffect(() => {
    if (!selectedNetworkCode) {
      form.setFieldValue(networkCountryCodeName, undefined)
      return
    }

    if (selectedNetwork?.countryCode) {
      form.setFieldValue(networkCountryCodeName, selectedNetwork.countryCode)
    }
  }, [form, networkCountryCodeName, selectedNetwork, selectedNetworkCode])

  return (
    <div className={className}>
      <Form.Item
        name={networkCountryCodeName}
        hidden
      />
      <CustomSelect
        className="w-full lg:w-1/3"
        label="Stablecoin"
        name={stablecoinName}
        options={currencies.map((currency) => ({
          label: currency.description || currency.code,
          value: currency.code,
        }))}
        placeholder="Select stablecoin"
        isLoading={isCurrenciesLoading}
        onChange={() => {
          form.setFieldValue(networkName, undefined)
          form.setFieldValue(networkCountryCodeName, undefined)
        }}
        required
      />
      <CustomSelect
        className="w-full lg:w-1/3"
        label="Network"
        name={networkName}
        options={networks
          .filter((network) => {
            // USDT is not supported on Base — hide only that combination (PRO-1243)
            const isUsdt = String(stablecoin ?? "").toUpperCase() === "USDT"
            const isBase = network.description?.toUpperCase() === "BASE"
            return !(isUsdt && isBase)
          })
          .map((network) => ({
            label: network.description,
            value: network.code,
          }))}
        placeholder={stablecoin ? "Select network" : "Select stablecoin first"}
        isLoading={isNetworksLoading}
        disabled={!stablecoin}
        required
      />
      <CustomInput
        className="w-full lg:w-1/3"
        label="Wallet address"
        name={walletAddressName}
        rules={walletAddressRules}
        required
      />
    </div>
  )
}

type StablecoinNetwork = {
  code: string
  countryCode?: string
  description: string
}

type CryptoCurrency = {
  code: string
  description: string
}

type CoreBackendResponse<T> = T | { data?: T }

const EVM_ADDRESS_PATTERN = /^0x[a-fA-F0-9]{40}$/
const TRON_ADDRESS_PATTERN = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
const SOLANA_ADDRESS_PATTERN = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/

const isValidStablecoinWalletAddress = (
  value: string,
  network?: StablecoinNetwork
) => {
  const address = value.trim()
  const networkLabel = `${network?.code || ""} ${
    network?.description || ""
  }`.toLowerCase()

  if (networkLabel.includes("tron") || networkLabel.includes("trc")) {
    return TRON_ADDRESS_PATTERN.test(address)
  }

  if (networkLabel.includes("solana") || networkLabel.includes("sol")) {
    return SOLANA_ADDRESS_PATTERN.test(address)
  }

  if (
    networkLabel.includes("ethereum") ||
    networkLabel.includes("erc") ||
    networkLabel.includes("bep") ||
    networkLabel.includes("bsc") ||
    networkLabel.includes("base") ||
    networkLabel.includes("polygon") ||
    networkLabel.includes("matic") ||
    networkLabel.includes("arbitrum") ||
    networkLabel.includes("optimism")
  ) {
    return EVM_ADDRESS_PATTERN.test(address)
  }

  return (
    EVM_ADDRESS_PATTERN.test(address) ||
    TRON_ADDRESS_PATTERN.test(address) ||
    SOLANA_ADDRESS_PATTERN.test(address)
  )
}

const getWalletAddressValidationMessage = (network?: StablecoinNetwork) => {
  const networkLabel = `${network?.code || ""} ${
    network?.description || ""
  }`.toLowerCase()

  if (networkLabel.includes("tron") || networkLabel.includes("trc")) {
    return "Enter a valid Tron wallet address."
  }

  if (networkLabel.includes("solana") || networkLabel.includes("sol")) {
    return "Enter a valid Solana wallet address."
  }

  if (
    networkLabel.includes("ethereum") ||
    networkLabel.includes("erc") ||
    networkLabel.includes("bep") ||
    networkLabel.includes("bsc") ||
    networkLabel.includes("base") ||
    networkLabel.includes("polygon") ||
    networkLabel.includes("matic") ||
    networkLabel.includes("arbitrum") ||
    networkLabel.includes("optimism")
  ) {
    return "Enter a valid EVM wallet address."
  }

  return "Enter a valid stablecoin wallet address."
}

const getResponseData = <T,>(
  response: CoreBackendResponse<T[]> | undefined
) => {
  if (!response) return []
  if (Array.isArray(response)) return response
  return Array.isArray(response.data) ? response.data : []
}

export default StablecoinPaymentFields
